// What a query is on the wire, and what the zones make of it. Declarations only,
// plus the three predicates that are pure statements about a shape.
//
// A query is an AND of groups; each group is a bag of interchangeable terms; one
// set of exclusions applies to the whole query. AND/OR is therefore not a
// control but a position — `+ term` is OR, `+ group` is AND, and `split`/`merge`
// move terms between the two.
import {
  QueryGroup,
  YukkaQuery,
} from 'src/app/shared/models/ai-search.interface';

// The root (YukkaQuery) and a group share a shape; only the root carries the
// referenced_* arrays.
export type QbNode = YukkaQuery | QueryGroup;

export const DEFAULT_SLOP = 5;

export type QbArtifactKind = 'query' | 'portfolio';

// How a group's own terms combine. 'any' is the normal bag-of-alternatives;
// 'all' is an AND node kept as it is rather than reshaped, and is what `split`
// turns into separate groups.
export type QbJoiner = 'any' | 'all';

export interface QbTerm {
  label: string; // what the pill shows
  value: string; // what the query matches on
  field: string;
  negated: boolean;
  path: number[]; // group indices from the root to the owning node
  index: number; // filter index within that node
  artifact: QbArtifactKind | null;
  volume: number | null; // verified branch volume, when a lexicon is supplied
}

/** Where a group's qualifying terms live: the node's path and what is in it. */
export interface QbZone {
  path: number[];
  /** Read from that node's own operator — a context bag can be an AND. */
  joiner: QbJoiner;
  terms: QbTerm[];
}

export interface QbGroup {
  joiner: QbJoiner;
  path: number[]; // [] = the root's own filters
  terms: QbTerm[];
  /**
   * Whether `splitGroup`/`mergeGroups` can act on this group at all. They only
   * handle a top-level node that owns nothing else, so without this the ⋮ menu
   * offered items that quietly did nothing.
   */
  restructurable: boolean;
  /**
   * Set when the group's own terms are too broad alone and only count alongside
   * one of these — a "grounded" group. Its presence is what makes it one, so
   * there is no separate kind to keep in step. An empty context is a block
   * still being filled in.
   */
  context: QbZone | null;
  /**
   * Exclusions scoped to this group's terms, not query-wide. Hoisting them into
   * the exclusions zone would change what the query means.
   */
  guard: QbZone | null;
  /**
   * Groups nested below this one — a child the grounded pattern has no slot for
   * (rule 7's "any other child is a sub-lane", and rule 8's AND of sub-lanes).
   * Recursive, so no depth is a special case, and drawn with the same nested
   * panel as a context block: it is the same statement one level further in.
   */
  subs: QbGroup[];
  /**
   * How those subs join this group's terms — read from the owning node's
   * operator, never assumed. A sub OR-ed onto its parent means something very
   * different from one AND-ed, and the panel says which.
   */
  subJoiner: 'and' | 'or';
}

export type QbBlockerKind =
  | 'inert-exclusion' // negated terms in an OR bag, which exclude nothing
  | 'mixed-node' // included and excluded terms share a node
  | 'unsupported-operator' // near, or anything that is not and/or
  | 'scoped-negation'; // an exclusion OR-ed in, which excludes nothing

export interface QbBlocker {
  kind: QbBlockerKind;
  path: number[];
  detail: string;
  /** The terms the blocker is about, when naming them helps the user. */
  terms?: string[];
}

/**
 * A saved query or portfolio the query embeds. The server resolves each one into
 * another child of the root, joined by the root's own operator — so it is a
 * block beside the groups, not a term inside one.
 */
export interface QbArtifactRef {
  kind: QbArtifactKind;
  id: string;
  name: string;
  negated: boolean;
  /** Position in `referenced_queries` / `referenced_portfolios`. */
  index: number;
}

export interface QbCounts {
  terms: number;
  excluded: number;
}

export interface QbModel {
  /**
   * The node whose children are the blocks below — where a new alternative has
   * to be added. Not always the root: a generated query wraps its alternatives
   * in one OR node so an exclusion can be AND-ed around them.
   */
  contentPath: number[];
  /**
   * How the *root* combines its children. Usually the same as `joiner`, but not
   * when a wrapper was stepped through — and artifacts hang off the root, so
   * their separator reads from this one.
   */
  rootJoiner: 'and' | 'or';
  // 'and' → every group must be mentioned (a collection query). 'or' → the
  // groups are alternatives (a company query: one logical group of name forms).
  // Read from the root operator, never assumed.
  joiner: 'and' | 'or';
  groups: QbGroup[];
  /** Embedded queries and portfolios that must match, beside the groups. */
  artifacts: QbArtifactRef[];
  /** …and the ones that must not, shown with the exclusions. */
  excludedArtifacts: QbArtifactRef[];
  exclusions: QbTerm[];
  exclusionsPath: number[] | null; // where the next exclusion goes; null → none yet
  counts: QbCounts;
  blockers: QbBlocker[];
  representable: boolean; // blockers.length === 0
}

// --- The structure view -----------------------------------------------------

/**
 * One node of the wire tree, mirrored 1:1 — no flattening, no peeling, no
 * blockers. The zones are a projection and cannot draw every shape; this can
 * draw all of them, so nothing has to be reshaped to stay editable.
 */
export interface QbNodeView {
  path: number[];
  /** Raw, so `near` and anything unrecognised still render as themselves. */
  operator: string;
  /** This node's own filters, in order, negated ones included. */
  terms: QbTerm[];
  children: QbNodeView[];
}

/** What a node in the structure view asks the host to do. */
export type QbNodeAction =
  | { kind: 'operator'; path: number[]; operator: 'and' | 'or' }
  | { kind: 'add-group'; path: number[] }
  | { kind: 'remove'; path: number[] };

// Fields whose `value` is the term itself. Every other field holds a resolved id
// (entity compound key, event id, source url, artifact uid) that the label does
// not match, so only these may be renamed in place — the rest are re-picked.
export function isFreeText(field: string | undefined): boolean {
  return field === 'text' || field === 'word';
}

// An embedded saved query or portfolio, lifted out of referenced_* into a filter
// so it can render as a pill among terms.
export function artifactKind(field: string | undefined): QbArtifactKind | null {
  return field === 'query' || field === 'portfolio' ? field : null;
}

/**
 * A grounded group with no term, or with no context term, cannot be searched —
 * the host blocks its re-run on this. Derived rather than stored, so it cannot
 * disagree with the terms beside it.
 */
export function isIncomplete(group: QbGroup): boolean {
  if (!group.context) return false;
  const has = (terms: QbTerm[]): boolean => terms.some((t) => t.value.trim());
  return !has(group.terms) || !has(group.context.terms);
}

/**
 * Whether anything in these groups or below them is incomplete — the question
 * the host's re-run gate asks. Kept apart from `isIncomplete`, which answers the
 * local question every block header asks: folding the subtree into it made a
 * parent whose own context is fine report "needs a context term" because a
 * sub-lane three levels down was empty.
 */
export function hasIncomplete(groups: QbGroup[]): boolean {
  return groups.some((g) => isIncomplete(g) || hasIncomplete(g.subs));
}
